import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'redirector.theme';

function read(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    // Private browsing, blocked site data — the default is still correct.
    return 'system';
  }
}

function apply(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

function systemPrefersDark(): boolean {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

/**
 * Applies the remembered theme before anything renders.
 *
 * `useTheme` only runs where the toggle is mounted, which is inside the signed
 * in shell — so the sign-in screen rendered in light for an operator who had
 * chosen dark, every time. This is called once at start-up instead, and the
 * toggle takes over from there.
 */
export function applyStoredTheme(): void {
  apply(read());
}

export function useTheme(): {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme: (next: Theme) => void;
} {
  const [theme, setThemeState] = useState<Theme>(read);

  useEffect(() => {
    apply(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A remembered preference is a convenience, not a requirement.
    }
  }, []);

  const resolved = theme === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : theme;
  return { theme, resolved, setTheme };
}
